import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpDown, Building2, CalendarRange, Download, RefreshCw, Search, Users,
} from 'lucide-react';
import api from '../../../api/client';
import { useUser } from '../../../contexts/userContext';
import PremiumSelect from '../component/PremiumSelect';
import RecruiterActivityDetail from './RecruiterActivityDetail';
import {
  PERIOD_OPTIONS,
  adminInitials,
  filterRecruiters,
  sortRecruiters,
  summaryCsv,
} from './recruiterActivityUtils';
import AptiviewLoader from '../../common/AptiviewLoader/AptiviewLoader';
import PageHeader, { PageCard, PageHeaderButton, PageHeaderStat, PageShell } from '../../common/PageHeader';

const SORT_OPTIONS = [
  { value: 'scheduled:desc', key: 'scheduled', direction: 'desc', labelKey: 'recruiterActivity.scheduled', fallback: 'Scheduled' },
  { value: 'completed:desc', key: 'completed', direction: 'desc', labelKey: 'recruiterActivity.completed', fallback: 'Completed' },
  { value: 'completion_rate:desc', key: 'completion_rate', direction: 'desc', labelKey: 'recruiterActivity.rate', fallback: 'Rate' },
  { value: 'name:asc', key: 'name', direction: 'asc', labelKey: 'recruiterActivity.name', fallback: 'Name' },
];

export default function RecruiterActivityPage() {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const searchInputRef = useRef(null);
  const columnRefs = useRef(new Map());
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (e) {
      return null;
    }
  }, []);
  const user = userDetails || storedUser;
  const isSuperAdmin = user?.is_superuser === true;
  const isOwner = user?.is_usermanager === true;
  const canView = isSuperAdmin || isOwner;

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [period, setPeriod] = useState('30d');
  const [query, setQuery] = useState('');
  const [viewFilter, setViewFilter] = useState('all');
  const [sortKey, setSortKey] = useState('scheduled');
  const [sortDirection, setSortDirection] = useState('desc');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadCompanies = async () => {
      try {
        const response = await api.get('companies/');
        const rows = Array.isArray(response.data)
          ? response.data
          : (response.data.results || []);
        const usable = rows.filter((company) => company?.id && company.name !== 'Practice Company');
        if (cancelled) return;
        setCompanies(usable);
        if (usable.length === 1) {
          setCompanyId(String(usable[0].id));
          return;
        }
        if (!isSuperAdmin && user?.admin_companies?.length) {
          const match = usable.find((company) => user.admin_companies.includes(company.name));
          if (match) setCompanyId(String(match.id));
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('recruiterActivity.companiesError', 'Could not load companies.'));
        }
      }
    };
    if (canView) loadCompanies();
    return () => { cancelled = true; };
  }, [canView, isSuperAdmin, t, user?.admin_companies]);

  const fetchSummary = useCallback(async () => {
    if (!canView || !companyId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        company_id: companyId,
        period,
      });
      const response = await api.get(`v1/recruiter-activity/summary/?${params.toString()}`);
      setSummary(response.data);
    } catch (err) {
      const statusCode = err.response?.status;
      if (statusCode === 403) {
        setError(t('recruiterActivity.forbidden', 'Only user managers can view recruiter activity.'));
      } else {
        setError(err.response?.data?.error || t('recruiterActivity.summaryError', 'Could not load recruiter activity.'));
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [canView, companyId, period, t]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== '/') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const allPeople = useMemo(() => summary?.recruiters || [], [summary]);
  const idleCount = allPeople.filter((row) => row.scheduled === 0).length;
  const activeCount = allPeople.filter((row) => row.scheduled > 0).length;

  const recruiters = useMemo(() => {
    const filtered = filterRecruiters(allPeople, query, viewFilter);
    return sortRecruiters(filtered, sortKey, sortDirection, false);
  }, [allPeople, query, viewFilter, sortKey, sortDirection]);

  const totals = useMemo(() => (
    allPeople.reduce(
      (acc, row) => {
        acc.scheduled += row.scheduled || 0;
        acc.completed += row.completed || 0;
        acc.expired += row.expired || 0;
        acc.idle += row.scheduled === 0 ? 1 : 0;
        acc.recruiters += 1;
        return acc;
      },
      { scheduled: 0, completed: 0, expired: 0, idle: 0, recruiters: 0 }
    )
  ), [allPeople]);

  const handleSortChange = (value) => {
    const option = SORT_OPTIONS.find((item) => item.value === value) || SORT_OPTIONS[0];
    setSortKey(option.key);
    setSortDirection(option.direction);
  };

  const scrollToColumn = (recruiterId) => {
    const node = columnRefs.current.get(recruiterId);
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const handleExport = () => {
    const csv = summaryCsv(recruiters);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recruiter_activity_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const companyOptions = companies.map((company) => ({
    value: String(company.id),
    label: company.name,
  }));
  const periodOptions = PERIOD_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey, option.fallback),
  }));
  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey, option.fallback),
  }));
  const showCompanyPicker = isSuperAdmin || companies.length > 1;
  const showBoard = Boolean(companyId);
  const isInitialLoading = loading && !summary;
  const viewTabs = [
    { value: 'all', label: t('recruiterActivity.viewAll', 'All'), count: allPeople.length },
    { value: 'active', label: t('recruiterActivity.viewActive', 'Active'), count: activeCount },
    { value: 'idle', label: t('recruiterActivity.viewIdle', 'Idle'), count: idleCount },
  ];

  if (!canView) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-xl rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800">
          <h1 className="text-lg font-semibold">{t('recruiterActivity.title', 'Recruiter Activity')}</h1>
          <p className="mt-2 text-sm">
            {t('recruiterActivity.forbidden', 'Only user managers can view recruiter activity.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageShell className="relative">
      <AptiviewLoader
        variant="content"
        active={isInitialLoading}
        message={t('common.loading', 'Loading…')}
      />
      <PageCard overflow={false} className="overflow-hidden">
          <PageHeader
            title={t('recruiterActivity.title', 'Recruiter Activity')}
            subtitle={t('recruiterActivity.subtitle', 'See who scheduled interviews, what completed, and who is idle.')}
          >
            {showBoard && (
              <PageHeaderStat
                label={t('recruiterActivity.viewIdle', 'Idle')}
                value={totals.idle}
              />
            )}
            <PageHeaderButton
              onClick={fetchSummary}
              disabled={loading || !companyId}
              icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
            >
              {t('common.refresh', 'Refresh')}
            </PageHeaderButton>
            <PageHeaderButton
              onClick={handleExport}
              disabled={!recruiters.length}
              color="sky"
              icon={<Download size={14} />}
            >
              {t('recruiterActivity.exportCsv', 'Export CSV')}
            </PageHeaderButton>
          </PageHeader>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-2.5 border-b border-gray-100 px-4 py-2.5 sm:px-6">
            {showCompanyPicker && (
              <div className="w-52">
                <PremiumSelect
                  label={t('recruiterActivity.company', 'Company')}
                  icon={Building2}
                  value={companyId}
                  onChange={setCompanyId}
                  options={companyOptions}
                  placeholder={t('recruiterActivity.selectCompany', 'Select a company')}
                  size="sm"
                />
              </div>
            )}
            <div className="w-44">
              <PremiumSelect
                label={t('recruiterActivity.period', 'Period')}
                icon={CalendarRange}
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                size="sm"
              />
            </div>
            <div className="w-44">
              <PremiumSelect
                label={t('recruiterActivity.sortBy', 'Sort by')}
                icon={ArrowUpDown}
                value={`${sortKey}:${sortDirection}`}
                onChange={handleSortChange}
                options={sortOptions}
                size="sm"
              />
            </div>
            <div className="relative w-56 sm:w-64">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('recruiterActivity.searchPlaceholder', 'Search by name')}
                className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-9 pr-8 text-xs text-gray-800 shadow-2xs placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
              <span className="absolute inset-y-0 right-2 flex items-center">
                <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-400">/</span>
              </span>
            </div>
            <div className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-xl border border-gray-200 bg-gray-100 p-1">
              {viewTabs.map((tab) => {
                const active = viewFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setViewFilter(tab.value)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all sm:text-sm ${
                      active
                        ? 'bg-blue-600 font-semibold text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                    <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold sm:text-xs ${
                      active ? 'bg-blue-700/60 text-white' : 'border border-gray-200 bg-white text-gray-700'
                    }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {showBoard && (
            <div className="flex flex-shrink-0 flex-wrap gap-2 border-b border-gray-100 px-4 py-2 sm:px-6">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
                <Users size={14} className="text-gray-400" />
                {totals.recruiters} {t('recruiterActivity.people', 'People')}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
                {totals.scheduled} {t('recruiterActivity.scheduled', 'Scheduled')}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-emerald-700">
                {totals.completed} {t('recruiterActivity.completed', 'Completed')}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
                {totals.idle} {t('recruiterActivity.viewIdle', 'Idle')}
              </span>
            </div>
          )}

          {error && (
            <div className="mx-4 mt-3 flex-shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}

          {!showBoard && (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-500">
              {t('recruiterActivity.pickCompany', 'Select a company to load recruiter activity.')}
            </div>
          )}

          {showBoard && !loading && recruiters.length === 0 && !error && !query && viewFilter === 'all' && (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-500">
              {t('recruiterActivity.noAdmins', 'No company admins found for this company.')}
            </div>
          )}

          {showBoard && recruiters.length === 0 && (query || viewFilter !== 'all') && (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-500">
              {t('recruiterActivity.noMatches', 'No people match this search or filter.')}
            </div>
          )}

          {showBoard && recruiters.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex flex-shrink-0 gap-2 overflow-x-auto border-b border-gray-100 px-4 py-2 sm:px-6">
                {recruiters.map((row) => {
                  const isIdle = row.scheduled === 0;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => scrollToColumn(row.id)}
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                        isIdle
                          ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-sky-300 hover:bg-sky-50'
                      }`}
                    >
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-[9px] font-semibold text-white">
                        {adminInitials(row.name)}
                      </span>
                      <span className="max-w-[8rem] truncate">{row.name}</span>
                      <span className={isIdle ? 'text-amber-700' : 'text-gray-500'}>{row.scheduled}</span>
                    </button>
                  );
                })}
              </div>
              <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
                <div className="flex h-full min-w-min gap-3 p-4">
                  {recruiters.map((row) => (
                    <div
                      key={row.id}
                      ref={(node) => {
                        if (node) columnRefs.current.set(row.id, node);
                        else columnRefs.current.delete(row.id);
                      }}
                      className="h-full w-[280px] flex-shrink-0"
                    >
                      <RecruiterActivityDetail
                        recruiter={row}
                        companyId={companyId}
                        period={period}
                        periodStart={summary?.period_start}
                        periodEnd={summary?.period_end}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
      </PageCard>
    </PageShell>
  );
}
